import React, {Fragment, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useRouteMatch} from 'react-router-dom';
import {Paginator, Preloader, useToast} from 'uikit';
import Form from '@/views/Admin/Events/Event/Promocodes/Form/Form';
import Toolbar from '@/views/Admin/Events/Event/Promocodes/Toolbar/Toolbar';
import Filter from '@/views/Admin/Events/Event/Promocodes/Filter/Filter';
import Table from '@/views/Admin/Events/Event/Promocodes/Table/Table';
import NotFound from '@/views/Admin/Events/Event/Promocodes/NotFound/NotFound';
import Confirm from '@/views/Admin/Events/Event/Promocodes/Confirm/Confirm';
import Tabs from '@/views/Admin/Events/Event/Promocodes/Tabs/Tabs';
import {PromocodesSchema} from '@/views/Admin/Events/Event/Promocodes/Promocodes.schema';
import {PromocodeGroupsService} from '@/services/PromocodeGroupsService/PromocodeGroupsService';
import {PromocodesService} from '@/services/PromocodesService/PromocodesService';
import {generateHash} from '@/utils/generateHash';
import scrollToElement from '@/utils/scrollToElement';
import {deletePromocodeResource} from '@/views/Admin/Events/Event/Promocodes/Promocode/Promocode.methods';
import {useQueryRouter} from '@/hooks/useQueryRouter/useQueryRouter';
import styles from './Promocodes.module.scss';
import {PromocodesServiceInterface} from "@/services/PromocodesService/PromocodesService.interface";
import {PromocodeGroupsServiceInterface} from "@/services/PromocodeGroupsService/PromocodeGroupsService.interface";
import {PromocodesInterface} from "@/views/Admin/Events/Event/Promocodes/Promocodes.interface";
import {PromocodesInternal} from "@/views/Admin/Events/Event/Promocodes/Promocodes.internal";
import {PromocodesMethods} from "@/views/Admin/Events/Event/Promocodes/Promocodes.methods";

const Promocodes = () => {
    const [data, setData] = useState({...PromocodesSchema, isLoading: true});
    const [confirm, setConfirm] = useState(false);
    const toast = useToast();
    const {search} = useLocation();
    const params = useMemo(() => new URLSearchParams(search), [search]);
    const match = useRouteMatch()
    const containerRef = useRef<HTMLDivElement | null>(null);
    const confirmAction = useRef<() => Promise<void>>(async () => null);
    const {currentPage, perPage} = PromocodesSchema.promocodes.meta;
    const page = {number: currentPage, size: perPage};
    const number = Number(params.get('page[number]')) || page.number;
    const size = Number(params.get('page[size]')) || page.size;
    const event = match.params.event_id || match.params.id
    const tabs = data.tabs;
    const content = tabs.includes(params.get('content') as string) ? params.get('content') : 'promocodes';
    const {query, set} = useQueryRouter({page: {number, size}, filter: {eventIds: [event]}, content});
    const current = content ? data[String(content)] : data.promocodes;
    const isFilterActive = useMemo(() => Object.values(query.filter ?? {}).some(v => v != null && v !== ''), [query.filter]);
    const isEmpty = !current.data.length && !isFilterActive && !data.isLoading && !current.isLoading;
    const isEmptyGlobal = !data.promocodes.data.length && !data.groups.data.length && !isFilterActive && !data.isLoading && !current.isLoading;
    const isNotFound = !current.data.length && isFilterActive;
    const isEdit = current.data.some((i: PromocodesServiceInterface | PromocodeGroupsServiceInterface) => i.isEdit);
    const isInit = data.isLoading && !current.meta.total && !current.data.length && !isFilterActive;
    const include = PromocodesInternal.getInclude(content).resources;
    const key = useMemo(() => JSON.stringify({...(query || {}), id: undefined}), [query]);

    useEffect(() => {
        handleFilter()
    }, [key]);

    const handleFilter = async () => {
        const number = query.page?.number || data[content].meta.currentPage
        const size = query.page?.size || data[content].meta.perPage
        const page = {number, size};
        const service = content === 'promocodes' ? PromocodesService : PromocodeGroupsService
        const config = {...query, page, ...(include.length ? ({include: [...include]}) : {})}
        delete config.content

        setData(prev => ({
            ...prev,
            isLoading: true,
            [content]: {...prev[content], isLoading: true},
        }))

        const response = await PromocodesMethods.getResourcesList(service.GetList, config);

        setData((p: PromocodesInterface) => ({
            ...p,
            isLoading: false,
            [content]: {
                ...p[content],
                isLoading: false,
                data: response.data.map((resource: any) => PromocodesInternal.getResource(content, resource)),
                meta: {...p[content].meta, ...response.meta},
            },
        }));

        scrollToElement(containerRef, {position: 'top', offset: -100});
    };

    const handleRemoveResources = (input) => {
        confirmAction.current = async () => {
            setData(p => ({...p, isLoading: true}));
            try {
                await Promise.all(
                    input.map(async (r) => {
                        if (content === 'promocodes') return await deletePromocodeResource(r);
                        if (content === 'groups') return await PromocodeGroupsService.Delete(r.id);
                    })
                );
                toast({type: 'success', message: 'Операция выполнена успешно'});
                handleFilter()
            } catch (e: any) {
                console.error(e)
                toast({type: 'danger', message: e.message || 'Произошла ошибка'});
            } finally {
                setData(p => ({...p, isLoading: false}));
            }
        };

        setConfirm(true);
    };

    const handleConfirm = async () => {
        await confirmAction.current();
        handleDecline();
    };

    const handleDecline = () => {
        setConfirm(false)
    }

    const handlePage = (number: number) => {
        set({...query, page: {number, size: query.page.size}})
    };
    const handleSize = (size: number) => {
        set({...query, page: {...page, size}})
    }

    const handleEditResource = async (id: number) => {
        const service = content === 'promocodes' ? PromocodesService : PromocodeGroupsService;
        const include = PromocodesInternal.getInclude(content).resource
        const config = {include};
        return await service.Get(id, config);
    };

    return (
        <div className={styles.container} ref={containerRef} data-container>
            {isInit && <Preloader/>}
            {!isInit && <>
                {!isEdit && <>
                    {!isEmptyGlobal && <Tabs
                        data={data}
                        onChange={setData}
                    />}
                    {!isEmpty &&
                        <Filter
                            data={data}
                            onReset={handleFilter}
                            onSubmit={handleFilter}
                            onChange={setData}
                        />
                    }
                </>}
                <div className={styles.table}>
                    {isEdit && <Form
                        data={data}
                        onChange={setData}
                        onRefresh={handleFilter}
                    />}
                    {!isEdit && <>
                        <Confirm
                            data={data}
                            onConfirm={confirm ? handleConfirm : undefined}
                            onDecline={handleDecline}/>
                        <Toolbar
                            data={data}
                            onChange={setData}
                            onRemove={handleRemoveResources}
                        />
                        {!isEmpty && isNotFound && <NotFound
                            data={data}
                            onReset={handleFilter}
                        />}
                        {!isEmpty && !isNotFound && <>
                            <Table
                                data={data}
                                onChange={setData}
                                onRemove={handleRemoveResources}
                                onEdit={handleEditResource}
                            />
                            <Paginator
                                disabled={data.isLoading}
                                page={Number(query.page.number)}
                                size={query.page.size}
                                last={current.meta.lastPage}
                                sizes={current.meta.sizes}
                                sibling={3}
                                theme="ghost"
                                type="secondary"
                                onPage={handlePage}
                                onSize={handleSize}
                            />
                        </>}
                    </>}
                </div>
            </>}
        </div>
    );
};

export default Promocodes

